/**
 * Employee Self-Service ("My HR") portal — current-user scoped.
 *
 * Phase 0: shell + identity header + tab strip. Each tab is a placeholder that
 * later phases fill (records, appraisals, quarterly quizzes, training, leave).
 * Available to any logged-in user whose account is linked to an HR employee;
 * the nav item itself is gated on `me.employee_id` in admin.js.
 */
import { escapeHtml } from '../../src/lib/format.js';
import * as api from '../api-client.js';
import * as notify from '../notifications.js';

const adminAPI = api;
const API = api.routes;
const showFlash = (msg, type) => notify.showFlash(msg, type);

let _me = null;
let _tab = 'records';
const _cache = {};

// In-progress timed quiz: { quizId, attemptId, questions, answers, deadline(ms),
// tick(intervalId), submitting, done }. Only one at a time.
let _activeQuiz = null;
let _exitHandlersBound = false;

function _kpi2(label, v) {
  return `<div style="min-width:90px;"><div class="muted" style="font-size:11px;">${_esc(label)}</div><strong style="font-size:18px;">${v == null ? '—' : _esc(v)}</strong></div>`;
}

function _stageBadges(approvals) {
  if (!approvals || !approvals.length) return '';
  const icon = (s) => (s === 'approved' ? '✓' : s === 'rejected' ? '✗' : '·');
  const cls = (s) => (s === 'approved' ? 'ok' : s === 'rejected' ? 'warn' : 'off');
  const label = (a) => escapeHtml(a.stage_label || (a.stage === 'hr' ? 'HR' : 'Mgr'));
  // Tooltip names who acted (once decided) or who is expected to (while pending).
  const who = (a) => {
    const name = a.approver_name || a.expected_approver;
    return name ? ` — ${a.approver_name ? '' : 'awaiting '}${name}` : '';
  };
  const pills = approvals
    .map((a) => `<span class="badge ${cls(a.status)}" style="font-size:10px; margin-left:4px;" title="${label(a)}${escapeHtml(who(a))}">${label(a)} ${icon(a.status)}</span>`)
    .join('');
  return `<div style="margin-top:2px;">${pills}</div>`;
}

function _renderLeave(box, data) {
  const bal = data.balance;
  const types = data.leave_types || ['annual'];
  const balCard = bal
    ? `<div style="display:flex; gap:18px; flex-wrap:wrap;">
         ${_kpi2('Available', bal.available_days)}${_kpi2('Balance', bal.balance_days)}
         ${_kpi2('Used', bal.used_days)}${_kpi2('Carried over', bal.carried_over_days)}
       </div>`
    : '<div class="muted" style="font-size:12px;">No leave balance set for this year — contact HR.</div>';
  const reqRows = (data.requests || []).map((r) => `<tr>
    <td>${_esc(r.start_date)} → ${_esc(r.end_date)}</td>
    <td>${_esc((r.leave_type || '').replace(/_/g, ' '))}</td>
    <td style="text-align:right;">${r.duration_days == null ? '—' : _esc(r.duration_days)}</td>
    <td>${_esc(r.status)}${_stageBadges(r.approvals)}</td>
  </tr>`).join('');
  box.innerHTML = `
    <div class="card" style="padding:12px; margin-bottom:12px;">
      <div style="font-weight:600; margin-bottom:6px;">Leave balance</div>${balCard}
    </div>
    <div class="card" style="padding:12px; margin-bottom:12px;">
      <div style="font-weight:600; margin-bottom:8px;">Request leave</div>
      <div class="row" style="gap:10px; flex-wrap:wrap; align-items:flex-end;">
        <label class="field"><span>From</span><input id="essLeaveStart" type="date" /></label>
        <label class="field"><span>To</span><input id="essLeaveEnd" type="date" /></label>
        <label class="field"><span>Type</span><select id="essLeaveType">${types.map((t) => `<option value="${_esc(t)}">${_esc(t.replace(/_/g, ' '))}</option>`).join('')}</select></label>
        <label style="display:inline-flex; align-items:center; gap:6px; font-size:12px;"><input type="checkbox" id="essLeaveHalf" /> Half day</label>
      </div>
      <label class="field" style="display:block; margin-top:8px;"><span>Reason</span><textarea id="essLeaveReason" rows="2"></textarea></label>
      <div id="essLeaveMsg" style="margin-top:6px; font-size:12px;"></div>
      <button type="button" class="btn primary" id="essLeaveSubmit" style="margin-top:8px;">Submit request</button>
      <div class="muted" style="font-size:11px; margin-top:4px;">Weekends and public holidays are not counted.</div>
    </div>
    <div class="card" style="padding:12px;">
      <div style="font-weight:600; margin-bottom:6px;">My requests</div>
      <div class="table-wrap"><table style="font-size:13px; width:100%;">
        <thead><tr><th>Dates</th><th>Type</th><th style="text-align:right;">Days</th><th>Status</th></tr></thead>
        <tbody>${reqRows || '<tr><td colspan="4" class="muted">No requests yet.</td></tr>'}</tbody>
      </table></div>
    </div>`;
  const btn = document.getElementById('essLeaveSubmit');
  if (btn) btn.addEventListener('click', _submitLeave);
}

async function _submitLeave() {
  const val = (id) => (document.getElementById(id)?.value || '').trim();
  const start = val('essLeaveStart');
  const end = val('essLeaveEnd');
  const msg = document.getElementById('essLeaveMsg');
  const setErr = (t) => { if (msg) { msg.textContent = t; msg.style.color = 'var(--err)'; } };
  if (!start || !end) { setErr('Pick both dates.'); return; }
  const payload = {
    start_date: start, end_date: end,
    leave_type: val('essLeaveType') || 'annual',
    half_day: !!document.getElementById('essLeaveHalf')?.checked,
    reason: val('essLeaveReason') || null,
  };
  const btn = document.getElementById('essLeaveSubmit');
  if (btn) btn.disabled = true;
  try {
    const res = await adminAPI.fetch(API.adminEssLeave, { method: 'POST', body: JSON.stringify(payload) });
    let note = `Requested ${res.duration_days} day(s).`;
    if (res.conflicts && res.conflicts.length) {
      note += ` ⚠ ${res.conflicts.length} teammate(s) also off then: ${res.conflicts.map((c) => c.employee_name).join(', ')}.`;
    }
    showFlash(note, res.conflicts && res.conflicts.length ? 'error' : 'success');
    delete _cache.leave;
    renderBody();
  } catch (err) {
    const d = err && err.body && err.body.detail;
    if (d && typeof d === 'object' && d.error === 'insufficient_balance') {
      setErr(`Not enough leave: requested ${d.requested_days}, available ${d.available_days}.`);
    } else {
      setErr(adminAPI.formatErrorDetail ? adminAPI.formatErrorDetail(err) : 'Request failed');
    }
    if (btn) btn.disabled = false;
  }
}

function _loanStageBadges(approvals) {
  if (!approvals || !approvals.length) return '';
  const icon = (s) => (s === 'approved' ? '✓' : s === 'denied' ? '✗' : '·');
  const cls = (s) => (s === 'approved' ? 'ok' : s === 'denied' ? 'warn' : 'off');
  const pills = approvals
    .map((a) => `<span class="badge ${cls(a.status)}" style="font-size:10px; margin-left:4px;">${_esc(a.label || a.stage)} ${icon(a.status)}</span>`)
    .join('');
  return `<div style="margin-top:4px;">${pills}</div>`;
}

function _renderLoans(box, data) {
  const loans = data.loans || [];
  const canRequest = !!data.can_request;
  const policy = data.policy || {};
  const maxTerm = policy.max_term_months;

  const loanRows = loans.map((l) => {
    const reps = (l.repayments || []).filter((r) => r.amount_paid != null);
    const repLine = reps.length
      ? `<div class="muted" style="font-size:11px; margin-top:4px;">${reps.length} repayment(s) · ${_esc(l.currency)} ${_esc(l.total_paid)} paid</div>`
      : '';
    const statusCls = l.status === 'approved' ? 'ok' : l.status === 'denied' ? 'warn' : 'off';
    return `<div class="card" style="padding:12px; margin-bottom:10px;">
      <div style="display:flex; justify-content:space-between; gap:12px; align-items:flex-start;">
        <div>
          <div style="font-weight:600;">${_esc(l.currency)} ${_esc(l.principal_amount)}
            <span class="badge ${statusCls}" style="font-size:10px; margin-left:6px;">${_esc(l.status)}</span>
          </div>
          <div class="muted" style="font-size:12px; margin-top:2px;">
            ${_esc(l.term_months)} month(s) · balance ${_esc(l.currency)} ${_esc(l.balance)}${l.collateral_leave_days ? ` · ${_esc(l.collateral_leave_days)} collateral day(s)` : ''}
          </div>
          ${_loanStageBadges(l.approvals)}
          ${repLine}
        </div>
      </div>
    </div>`;
  }).join('');

  const requestCard = canRequest
    ? `<div class="card" style="padding:12px; margin-bottom:12px;">
         <div style="font-weight:600; margin-bottom:8px;">Request a loan</div>
         <div class="row" style="gap:10px; flex-wrap:wrap; align-items:flex-end;">
           <label class="field"><span>Amount</span><input id="essLoanAmount" type="number" min="1" step="0.01" /></label>
           <label class="field"><span>Currency</span><input id="essLoanCurrency" type="text" value="GMD" maxlength="8" style="width:80px;" /></label>
           <label class="field"><span>Term (months)</span><input id="essLoanTerm" type="number" min="1" step="1" /></label>
           <label class="field"><span>Collateral leave days</span><input id="essLoanCollateral" type="number" min="0" step="0.5" /></label>
         </div>
         <label class="field" style="display:block; margin-top:8px;"><span>Reason</span><textarea id="essLoanReason" rows="2"></textarea></label>
         <div id="essLoanMsg" style="margin-top:6px; font-size:12px;"></div>
         <button type="button" class="btn primary" id="essLoanSubmit" style="margin-top:8px;">Submit request</button>
         ${maxTerm ? `<div class="muted" style="font-size:11px; margin-top:4px;">Maximum term: ${_esc(maxTerm)} months.</div>` : ''}
       </div>`
    : `<div class="card" style="padding:12px; margin-bottom:12px;"><div class="muted" style="font-size:12px;">You have a loan in progress — you can request another once it is fully repaid or closed.</div></div>`;

  box.innerHTML = `
    ${requestCard}
    <div class="card" style="padding:12px;">
      <div style="font-weight:600; margin-bottom:8px;">My loans</div>
      ${loanRows || '<div class="muted" style="font-size:12px;">No loans yet.</div>'}
    </div>`;
  const btn = document.getElementById('essLoanSubmit');
  if (btn) btn.addEventListener('click', _submitLoan);
}

async function _submitLoan() {
  const val = (id) => (document.getElementById(id)?.value || '').trim();
  const amount = parseFloat(val('essLoanAmount'));
  const term = parseInt(val('essLoanTerm'), 10);
  const msg = document.getElementById('essLoanMsg');
  const setErr = (t) => { if (msg) { msg.textContent = t; msg.style.color = 'var(--err)'; } };
  if (!(amount > 0)) { setErr('Enter a valid amount.'); return; }
  if (!(term > 0)) { setErr('Enter a valid term in months.'); return; }
  const collateral = val('essLoanCollateral');
  const payload = {
    principal_amount: amount,
    term_months: term,
    collateral_leave_days: collateral === '' ? null : parseFloat(collateral),
    currency: val('essLoanCurrency') || 'GMD',
    reason: val('essLoanReason') || null,
  };
  const btn = document.getElementById('essLoanSubmit');
  if (btn) btn.disabled = true;
  try {
    await adminAPI.fetch(API.adminEssLoans, { method: 'POST', body: JSON.stringify(payload) });
    showFlash('Loan request submitted for approval.', 'success');
    delete _cache.loans;
    renderBody();
  } catch (err) {
    const d = err && err.body && err.body.detail;
    if (d && typeof d === 'object' && d.error === 'term_exceeds_policy') {
      setErr(`Term too long: maximum is ${d.max_term_months} months.`);
    } else if (d && typeof d === 'object' && d.error === 'deduction_exceeds_policy') {
      setErr(`Repayment too high: it would take ${d.would_deduct_pct}% of your pay (max ${d.max_deduction_pct}%). Increase the term or lower the amount.`);
    } else if (d === 'loan_already_open') {
      setErr('You already have a loan in progress.');
    } else {
      setErr(adminAPI.formatErrorDetail ? adminAPI.formatErrorDetail(err) : 'Request failed');
    }
    if (btn) btn.disabled = false;
  }
}

const TAB_ROUTES = {
  records: 'adminEssProfile',
  training: 'adminEssTraining',
  appraisals: 'adminEssReviews',
  policies: 'adminEssPolicies',
  quizzes: 'adminEssQuizzes',
  approvals: 'adminEssApprovals',
};

function _esc(v) { return escapeHtml(v == null ? '' : String(v)); }
function _date(v) { return v ? _esc(v) : '—'; }

function _kv(label, value) {
  return `<div style="display:flex; gap:8px; padding:4px 0; border-bottom:1px dashed var(--border,#eee);">
    <span class="muted" style="flex:0 0 150px; font-size:12px;">${_esc(label)}</span>
    <span style="flex:1; font-size:13px;">${value == null || value === '' ? '—' : _esc(value)}</span>
  </div>`;
}

function _renderRecords(box, p) {
  box.innerHTML = `<div class="card" style="padding:14px; max-width:640px;">
    ${_kv('Name', p.full_name)}
    ${_kv('Email', p.email)}
    ${_kv('Phone', p.phone)}
    ${_kv('Department', p.department_name)}
    ${_kv('Position', p.position_name)}
    ${_kv('Manager', p.manager_name)}
    ${_kv('Location', p.location_name)}
    ${_kv('Employment', p.employment_type)}
    ${_kv('Status', p.status)}
    ${_kv('Hire date', p.hire_date)}
    <div class="muted" style="font-size:11px; margin-top:8px;">To correct any detail, submit a change request via HR.</div>
  </div>`;
}

function _renderTraining(box, data) {
  const enr = data.enrollments || [];
  const rows = enr.map((e) => `<tr>
    <td>${_esc(e.course_title)}</td>
    <td>${_esc(e.category)}</td>
    <td>${_esc((e.status || '').replace(/_/g, ' '))}</td>
    <td style="text-align:right;">${e.score == null ? '—' : _esc(e.score)}</td>
    <td>${_date(e.completed_at)}</td>
  </tr>`).join('');
  const certs = (data.certifications || []).map((c) => `<tr>
    <td>${_esc(c.name)}</td><td>${_date(c.issue_date)}</td><td>${_date(c.expiry_date)}</td>
  </tr>`).join('');
  box.innerHTML = `
    <div class="card" style="padding:12px; margin-bottom:12px;">
      <div style="font-weight:600; margin-bottom:6px;">Training <span class="muted" style="font-size:12px; font-weight:400;">· ${data.completed_count} completed of ${data.total_count}</span></div>
      <div class="table-wrap"><table style="font-size:13px; width:100%;">
        <thead><tr><th>Course</th><th>Category</th><th>Status</th><th style="text-align:right;">Score</th><th>Completed</th></tr></thead>
        <tbody>${rows || '<tr><td colspan="5" class="muted">No training on record.</td></tr>'}</tbody>
      </table></div>
    </div>
    <div class="card" style="padding:12px;">
      <div style="font-weight:600; margin-bottom:6px;">Certifications</div>
      <div class="table-wrap"><table style="font-size:13px; width:100%;">
        <thead><tr><th>Certification</th><th>Issued</th><th>Expires</th></tr></thead>
        <tbody>${certs || '<tr><td colspan="3" class="muted">No certifications.</td></tr>'}</tbody>
      </table></div>
    </div>`;
}

function renderHeader() {
  const el = document.getElementById('essPortalHeader');
  if (!el) return;
  if (!_me) { el.innerHTML = ''; return; }
  const bits = [
    _me.position_name, _me.department_name,
    _me.status ? `status: ${_me.status}` : null,
  ].filter(Boolean).map((b) => escapeHtml(b)).join(' · ');
  el.innerHTML = `
    <div class="card" style="padding:12px;">
      <div style="font-size:16px; font-weight:700;">${escapeHtml(_me.full_name || 'My HR')}</div>
      <div class="muted" style="font-size:12px; margin-top:2px;">${bits || 'Employee'}</div>
    </div>`;
}

const _STATUS_LABEL = { submitted: 'In progress', reviewed: 'Reviewed', closed: 'Closed' };

function _renderAppraisals(box, data) {
  const reviews = data.reviews || [];
  const rows = reviews.map((r) => {
    const finalized = r.status === 'reviewed' || r.status === 'closed';
    return `<tr>
      <td>${_esc(r.period || '')}${r.period_end ? `<div class="muted" style="font-size:11px;">to ${_esc(r.period_end)}</div>` : ''}</td>
      <td>${_esc(_STATUS_LABEL[r.status] || r.status)}</td>
      <td style="text-align:right; font-weight:${finalized ? 600 : 400};">${r.final_score == null ? '—' : _esc(r.final_score)}</td>
      <td>${_esc((r.decision || '').replace(/_/g, ' ')) || '—'}</td>
      <td>${_date(r.reviewed_at ? r.reviewed_at.slice(0, 10) : null)}</td>
    </tr>`;
  }).join('');
  // Latest finalized review's narrative, if any.
  const latest = reviews.find((r) => r.status === 'reviewed' || r.status === 'closed');
  const narrative = latest && (latest.strengths || latest.improvements)
    ? `<div class="card" style="padding:12px;">
        ${latest.strengths ? `<div style="margin-bottom:6px;"><strong style="font-size:12px;">Strengths</strong><div style="font-size:13px;">${_esc(latest.strengths)}</div></div>` : ''}
        ${latest.improvements ? `<div><strong style="font-size:12px;">Areas to improve</strong><div style="font-size:13px;">${_esc(latest.improvements)}</div></div>` : ''}
      </div>` : '';
  box.innerHTML = `
    <div class="card" style="padding:12px; margin-bottom:12px;">
      <div style="display:flex; align-items:baseline; gap:8px;">
        <span style="font-weight:600;">Appraisals</span>
        <span class="muted" style="font-size:12px;">Next due: <strong>${_esc(data.next_due || '—')}</strong> · ${_esc(data.cadence || 'quarterly')}</span>
      </div>
      <div class="table-wrap" style="margin-top:8px;"><table style="font-size:13px; width:100%;">
        <thead><tr><th>Period</th><th>Status</th><th style="text-align:right;">Final score</th><th>Decision</th><th>Reviewed</th></tr></thead>
        <tbody>${rows || '<tr><td colspan="5" class="muted">No appraisals on record yet.</td></tr>'}</tbody>
      </table></div>
    </div>
    ${narrative}`;
}

// ---- Quizzes (quarterly, timed, one attempt per quarter) ------------------

function _quizAttemptSummary(a) {
  if (!a) return '';
  if (a.status === 'in_progress') return '<span style="color:var(--warn,#b8860b);">In progress…</span>';
  const verdict = a.passed ? '<span style="color:var(--ok,#2e7d32);">Passed</span>' : '<span style="color:var(--err,#c62828);">Not passed</span>';
  const tag = a.status === 'expired' ? ' <span class="muted">(timed out)</span>' : '';
  return `${a.score == null ? '—' : _esc(a.score)}% · ${verdict}${tag}`;
}

function _renderQuizList(box, data) {
  const quizzes = data.quizzes || [];
  const rows = quizzes.map((q) => {
    const done = !q.due_this_quarter;
    const attempt = q.current_attempt;
    const noQuestions = !done && !(Number(q.question_count) > 0);
    const action = done
      ? `<span class="muted" style="font-size:12px;">Done this quarter</span>`
      : noQuestions
        ? `<span class="muted" style="font-size:12px; text-align:right; display:inline-block;">Not ready yet —<br>no questions added.<br>Contact HR.</span>`
        : `<button type="button" class="btn primary" data-ess-quiz-start="${_esc(q.id)}">Do test</button>`;
    const hist = (q.history || []).filter((h) => !attempt || h.id !== attempt.id).slice(0, 4)
      .map((h) => `<span class="muted" style="font-size:11px;">${_esc(h.quarter)}: ${h.score == null ? '—' : _esc(h.score)}%</span>`).join(' · ');
    return `<div class="card" style="padding:12px; margin-bottom:10px;">
      <div style="display:flex; justify-content:space-between; align-items:flex-start; gap:12px;">
        <div>
          <div style="font-weight:600;">${_esc(q.title)}</div>
          <div class="muted" style="font-size:12px; margin-top:2px;">
            ${_esc(q.question_count)} question(s) · ${_esc(q.time_limit_minutes)} min · pass ${_esc(q.pass_mark)}%
          </div>
          <div style="font-size:12px; margin-top:4px;">${done ? _quizAttemptSummary(attempt) : `<span class="muted">Due this quarter (by ${_esc(q.due_date)})</span>`}</div>
          ${hist ? `<div style="margin-top:4px;">${hist}</div>` : ''}
        </div>
        <div style="flex:0 0 auto;">${action}</div>
      </div>
    </div>`;
  }).join('');
  box.innerHTML = `
    <div style="margin-bottom:10px; font-size:12px;" class="muted">
      Quarter <strong>${_esc(data.quarter)}</strong>. Each quiz is a <strong>timed, one-shot</strong> test —
      once you start you cannot pause, resume, or retake it until next quarter.
    </div>
    ${rows || '<div class="card" style="padding:16px;"><div class="muted">No policy quizzes assigned to you.</div></div>'}`;
  box.querySelectorAll('[data-ess-quiz-start]').forEach((b) => {
    b.addEventListener('click', () => {
      const quiz = quizzes.find((q) => q.id === b.getAttribute('data-ess-quiz-start'));
      if (quiz) _openQuizWarning(quiz);
    });
  });
}

function _renderQuizzes(box, data) {
  if (_activeQuiz && !_activeQuiz.done) { _renderActiveQuiz(box); return; }
  _renderQuizList(box, data);
}

function _closeModal() {
  const m = document.getElementById('essQuizModal');
  if (m) m.remove();
}

function _openQuizWarning(quiz) {
  _closeModal();
  const overlay = document.createElement('div');
  overlay.id = 'essQuizModal';
  overlay.style.cssText = 'position:fixed; inset:0; background:rgba(0,0,0,0.45); z-index:9999; display:flex; align-items:center; justify-content:center; padding:16px;';
  overlay.innerHTML = `
    <div class="card" style="max-width:480px; padding:20px;">
      <div style="font-size:16px; font-weight:700; margin-bottom:8px;">Before you start: ${_esc(quiz.title)}</div>
      <ul style="font-size:13px; line-height:1.6; padding-left:18px; margin:0 0 12px;">
        <li>You have <strong>${_esc(quiz.time_limit_minutes)} minutes</strong> to answer <strong>${_esc(quiz.question_count)}</strong> question(s).</li>
        <li>Once started, the timer <strong>cannot be paused</strong>.</li>
        <li>If you leave, close the tab, or the time runs out, the test is <strong>submitted and scored automatically</strong>.</li>
        <li>You <strong>cannot retake</strong> it until next quarter.</li>
        <li>Only start when you will be <strong>undisturbed for ${_esc(quiz.time_limit_minutes)} minutes</strong>.</li>
      </ul>
      <div id="essQuizWarnMsg" style="font-size:12px; color:var(--err); min-height:16px;"></div>
      <div style="display:flex; gap:8px; justify-content:flex-end; margin-top:8px;">
        <button type="button" class="btn" id="essQuizCancel">Cancel</button>
        <button type="button" class="btn primary" id="essQuizBegin">I'm ready — start now</button>
      </div>
    </div>`;
  document.body.appendChild(overlay);
  overlay.addEventListener('click', (e) => { if (e.target === overlay) _closeModal(); });
  document.getElementById('essQuizCancel').addEventListener('click', _closeModal);
  document.getElementById('essQuizBegin').addEventListener('click', () => _startQuiz(quiz));
}

async function _startQuiz(quiz) {
  const begin = document.getElementById('essQuizBegin');
  const msg = document.getElementById('essQuizWarnMsg');
  if (begin) begin.disabled = true;
  try {
    const res = await adminAPI.fetch(API.adminEssQuizStart(quiz.id), { method: 'POST', body: JSON.stringify({}) });
    _closeModal();
    const questions = res.questions || [];
    _activeQuiz = {
      quizId: quiz.id,
      attemptId: res.attempt_id,
      title: res.title || quiz.title,
      questions,
      answers: new Array(questions.length).fill(null),
      deadline: new Date(res.expires_at).getTime(),
      passMark: res.pass_mark,
      tick: null,
      saveTimer: null,
      saving: false,
      submitting: false,
      done: false,
    };
    _bindExitHandlers();
    renderBody();
  } catch (err) {
    const d = err && err.body && err.body.detail;
    if (err && err.status === 409 && d && typeof d === 'object' && d.error === 'already_attempted') {
      if (msg) msg.textContent = 'You have already taken this quiz this quarter.';
    } else if (err && err.status === 409 && d === 'quiz_has_no_questions') {
      if (msg) msg.textContent = 'This test is not ready yet — no questions have been added. Please contact HR.';
    } else if (msg) {
      msg.textContent = adminAPI.formatErrorDetail ? adminAPI.formatErrorDetail(err) : 'Could not start the test.';
    }
    if (begin) begin.disabled = false;
  }
}

function _fmtRemaining(ms) {
  if (ms < 0) ms = 0;
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  return `${String(m).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}

function _renderActiveQuiz(box) {
  const q = _activeQuiz;
  const questions = q.questions.map((qq, i) => {
    const opts = (qq.options || []).map((opt, oi) => `
      <label style="display:block; padding:4px 0; font-size:13px; cursor:pointer;">
        <input type="radio" name="essQ${i}" value="${oi}" ${q.answers[i] === oi ? 'checked' : ''} /> ${_esc(opt)}
      </label>`).join('');
    return `<div class="card" style="padding:12px; margin-bottom:10px;">
      <div style="font-weight:600; font-size:13px; margin-bottom:6px;">${i + 1}. ${_esc(qq.text)}</div>
      ${opts}
    </div>`;
  }).join('');
  box.innerHTML = `
    <div style="position:sticky; top:0; z-index:5; background:var(--bg,#fff); padding:8px 0; margin-bottom:8px; border-bottom:1px solid var(--border,#eee); display:flex; justify-content:space-between; align-items:center;">
      <div style="font-weight:700;">${_esc(q.title)}</div>
      <div style="display:flex; gap:12px; align-items:center;">
        <span id="essQuizSaved" class="muted" style="font-size:11px;"></span>
        <div style="font-size:15px; font-weight:700;">⏳ <span id="essQuizTimer">--:--</span></div>
      </div>
    </div>
    <div class="muted" style="font-size:11px; margin-bottom:8px;">Answers save as you go. Leaving this page submits your test.</div>
    <div id="essQuizQuestions">${questions}</div>
    <div id="essQuizMsg" style="font-size:12px; min-height:16px;"></div>
    <button type="button" class="btn primary" id="essQuizSubmit" style="margin:6px 0 24px;">Submit test</button>`;
  box.querySelectorAll('#essQuizQuestions input[type=radio]').forEach((el) => {
    el.addEventListener('change', (e) => {
      const i = parseInt(e.target.name.slice(4), 10);
      _activeQuiz.answers[i] = parseInt(e.target.value, 10);
      _setSavedIndicator('Saving…');
      _scheduleAutosave();
    });
  });
  const submitBtn = document.getElementById('essQuizSubmit');
  if (submitBtn) submitBtn.addEventListener('click', () => _submitActiveQuiz('manual'));
  _startTimer();
}

function _setSavedIndicator(text) {
  const el = document.getElementById('essQuizSaved');
  if (el) el.textContent = text;
}

// Debounced real-time autosave: persists the current answers to the server (which
// updates the live gradebook) without finalizing, so progress survives a crash.
function _scheduleAutosave() {
  const q = _activeQuiz;
  if (!q || q.done) return;
  if (q.saveTimer) clearTimeout(q.saveTimer);
  q.saveTimer = setTimeout(_autosaveActiveQuiz, 700);
}

async function _autosaveActiveQuiz() {
  const q = _activeQuiz;
  if (!q || q.done || q.submitting) return;
  q.saving = true;
  try {
    const res = await adminAPI.fetch(API.adminEssQuizAnswer(q.quizId), {
      method: 'POST', body: JSON.stringify({ answers: q.answers }),
    });
    if (res && res.finalized) {
      // Server finalized on expiry mid-typing — stop the quiz.
      q.done = true;
      _unbindExitHandlers();
      if (q.tick) { clearInterval(q.tick); q.tick = null; }
      const r = res.result || {};
      showFlash(`Time up — test auto-submitted (${r.score == null ? '—' : r.score}%).`, r.passed ? 'success' : 'error');
      _activeQuiz = null;
      delete _cache.quizzes;
      if (_tab === 'quizzes') renderBody();
      return;
    }
    _setSavedIndicator('Saved ✓');
  } catch {
    // Best-effort — the exit beacon + server-side expiry finalize are the backstops.
    _setSavedIndicator('Save failed — will retry');
  } finally {
    q.saving = false;
  }
}

function _startTimer() {
  const q = _activeQuiz;
  if (!q) return;
  if (q.tick) clearInterval(q.tick);
  const update = () => {
    const el = document.getElementById('essQuizTimer');
    const remaining = q.deadline - Date.now();
    if (el) {
      el.textContent = _fmtRemaining(remaining);
      el.style.color = remaining < 60000 ? 'var(--err,#c62828)' : '';
    }
    if (remaining <= 0) {
      clearInterval(q.tick);
      q.tick = null;
      _submitActiveQuiz('timeout');
    }
  };
  update();
  q.tick = setInterval(update, 1000);
}

async function _submitActiveQuiz(reason) {
  const q = _activeQuiz;
  if (!q || q.submitting || q.done) return;
  q.submitting = true;
  if (q.tick) { clearInterval(q.tick); q.tick = null; }
  if (q.saveTimer) { clearTimeout(q.saveTimer); q.saveTimer = null; }
  const btn = document.getElementById('essQuizSubmit');
  if (btn) { btn.disabled = true; btn.textContent = 'Submitting…'; }
  try {
    const res = await adminAPI.fetch(API.adminEssQuizSubmit(q.quizId), {
      method: 'POST', body: JSON.stringify({ answers: q.answers }),
    });
    q.done = true;
    _unbindExitHandlers();
    const r = res.result || {};
    const verdict = r.passed ? 'passed ✅' : 'not passed ❌';
    const timedOut = res.expired || r.status === 'expired';
    showFlash(`Test ${timedOut ? 'auto-submitted (time up)' : 'submitted'} — ${r.score == null ? '—' : r.score}% (${verdict}).`, r.passed ? 'success' : 'error');
    _activeQuiz = null;
    delete _cache.quizzes;
    if (_tab === 'quizzes') renderBody();
  } catch (err) {
    q.submitting = false;
    // Already finalized server-side (e.g. beacon on a prior exit) — treat as done.
    if (err && err.status === 404) {
      q.done = true;
      _unbindExitHandlers();
      _activeQuiz = null;
      delete _cache.quizzes;
      if (_tab === 'quizzes') renderBody();
      return;
    }
    const msg = document.getElementById('essQuizMsg');
    if (msg) { msg.style.color = 'var(--err)'; msg.textContent = adminAPI.formatErrorDetail ? adminAPI.formatErrorDetail(err) : 'Submit failed — try again.'; }
    if (btn) { btn.disabled = false; btn.textContent = 'Submit test'; }
    _startTimer();
  }
}

// Auto-submit on tab close / navigation away via a keepalive beacon so the
// attempt is scored even if the user never clicks Submit.
function _beaconSubmit() {
  const q = _activeQuiz;
  if (!q || q.submitting || q.done) return;
  try {
    const url = API.adminEssQuizSubmit(q.quizId);
    const body = new Blob([JSON.stringify({ answers: q.answers })], { type: 'application/json' });
    if (navigator.sendBeacon) navigator.sendBeacon(url, body);
  } catch { /* best-effort */ }
}

function _onPageHide() { _beaconSubmit(); }
function _onBeforeUnload(e) {
  if (_activeQuiz && !_activeQuiz.done) {
    _beaconSubmit();
    e.preventDefault();
    e.returnValue = '';
    return '';
  }
}

function _bindExitHandlers() {
  if (_exitHandlersBound) return;
  _exitHandlersBound = true;
  window.addEventListener('pagehide', _onPageHide);
  window.addEventListener('beforeunload', _onBeforeUnload);
}

function _unbindExitHandlers() {
  if (!_exitHandlersBound) return;
  _exitHandlersBound = false;
  window.removeEventListener('pagehide', _onPageHide);
  window.removeEventListener('beforeunload', _onBeforeUnload);
}

// ---- Leave approvals (approver self-service) ------------------------------

function _setApprovalBadges(n) {
  [document.getElementById('essApprovalsNavBadge'), document.getElementById('essApprovalsTabBadge')].forEach((el) => {
    if (!el) return;
    el.textContent = n > 0 ? String(n) : '';
    el.style.display = n > 0 ? '' : 'none';
  });
}

function _renderApprovals(box, data) {
  const items = data.items || [];
  _setApprovalBadges(items.length);
  if (!items.length) {
    box.innerHTML = '<div class="card" style="padding:16px;"><div class="muted">Nothing is waiting for your approval.</div></div>';
    return;
  }
  const detail = (it) => {
    if (it.type === 'loan') {
      return `${_esc(it.currency)} ${_esc(it.principal_amount)} · ${_esc(it.term_months)} month(s)`;
    }
    return `${_esc(it.start_date)} → ${_esc(it.end_date)} · ${it.duration_days == null ? '—' : _esc(it.duration_days)} day(s) · ${_esc((it.leave_type || '').replace(/_/g, ' '))}`;
  };
  const typeBadge = (it) => (it.type === 'loan' ? '💰 Loan' : '🏝 Leave');
  const stageName = (it) => (it.stage_label || (it.stage === 'hr' ? 'HR stage' : it.stage));
  // The full approver chain (co-approvers + their decisions), for both leave and loan.
  const chainLine = (it) => {
    const arr = it.approvals || [];
    if (!arr.length) return '';
    const rows = arr.map((a) => {
      const s = (a.status || 'pending').toLowerCase();
      const mark = s === 'approved' ? '✓' : (s === 'rejected' || s === 'denied') ? '✗' : '·';
      const stageLbl = a.stage_label || a.label || a.stage;
      const who = a.approver_name
        || a.expected_approver
        || (Array.isArray(a.expected_approvers) ? a.expected_approvers.join(', ') : '');
      return `<div style="font-size:11px; color:var(--muted, #64748b);">${mark} ${_esc(stageLbl)}${who ? ' · ' + _esc(who) : ''}</div>`;
    }).join('');
    return `<div style="margin-top:6px;">${rows}</div>`;
  };
  const rows = items.map((it) => `
    <div class="card" style="padding:12px; margin-bottom:10px;" data-appr-card="${_esc(it.approval_id)}">
      <div style="display:flex; justify-content:space-between; gap:12px; align-items:flex-start;">
        <div>
          <div style="font-weight:600;">${_esc(it.applicant_name || 'Employee')}
            <span class="badge" style="font-size:10px;">${typeBadge(it)}</span>
            <span class="badge" style="font-size:10px;">${_esc(stageName(it))}</span>
          </div>
          <div class="muted" style="font-size:12px; margin-top:2px;">${detail(it)}</div>
          ${it.reason ? `<div style="font-size:12px; margin-top:4px;">${_esc(it.reason)}</div>` : ''}
          ${chainLine(it)}
        </div>
        <div style="display:flex; gap:6px; flex:0 0 auto; flex-wrap:wrap; justify-content:flex-end;">
          ${it.type === 'leave' ? `<button type="button" class="btn" data-appr-analysis="${_esc(it.request_id)}" title="Who else in the department is off over this period">Analysis</button>` : ''}
          <button type="button" class="btn" data-appr-reject="${_esc(it.approval_id)}">Reject</button>
          <button type="button" class="btn primary" data-appr-approve="${_esc(it.approval_id)}">Approve</button>
        </div>
      </div>
    </div>`).join('');
  box.innerHTML = `<div class="muted" style="font-size:12px; margin-bottom:8px;">Each stage must approve before a request is granted.</div>${rows}`;
  box.querySelectorAll('[data-appr-approve]').forEach((b) => b.addEventListener('click', () => _decideApproval(b.getAttribute('data-appr-approve'), 'approve')));
  box.querySelectorAll('[data-appr-reject]').forEach((b) => b.addEventListener('click', () => _decideApproval(b.getAttribute('data-appr-reject'), 'reject')));
  box.querySelectorAll('[data-appr-analysis]').forEach((b) => b.addEventListener('click', () => _openLeaveAnalysis(b.getAttribute('data-appr-analysis'))));
}

async function _openLeaveAnalysis(requestId) {
  if (!requestId) return;
  let data;
  try {
    data = await adminAPI.fetch(API.adminEssLeaveAnalysis(requestId), { method: 'GET' });
  } catch (err) {
    showFlash(adminAPI.formatErrorDetail ? adminAPI.formatErrorDetail(err) : 'Could not load analysis.', 'error');
    return;
  }
  _showLeaveAnalysisModal(data);
}

function _showLeaveAnalysisModal(data) {
  const dept = data.department || {};
  const req = data.request || {};
  const roster = Array.isArray(data.roster) ? data.roster : [];
  const overlaps = Array.isArray(data.overlaps) ? data.overlaps : [];
  const cap = data.capacity_warning;

  const capBanner = cap
    ? `<div class="badge warn" style="display:block; padding:8px 10px; font-size:12px; margin-bottom:10px;">
         <strong>Over department capacity</strong> — up to ${_esc(String(Math.max(...cap.days.map((d) => d.count))))} off on some days (cap ${_esc(String(cap.max_concurrent))}): ${cap.days.map((d) => `${_esc(d.date)} (${d.count})`).join(' · ')}
       </div>`
    : `<div class="muted" style="font-size:12px; margin-bottom:10px;">Within department capacity${dept.max_concurrent != null ? ` (cap ${_esc(String(dept.max_concurrent))})` : ''}.</div>`;

  const overlapBox = overlaps.length
    ? `<div style="font-size:11px; text-transform:uppercase; letter-spacing:.04em; opacity:.7; margin:4px 0;">Overlapping this window (${overlaps.length})</div>
       ${overlaps.map((o) => `<div style="font-size:13px; padding:2px 0;">• ${_esc(o.employee_name)} <span class="muted">${_esc(o.start_date)} → ${_esc(o.end_date)} · ${_esc(o.status)}</span></div>`).join('')}`
    : `<div class="muted" style="font-size:13px;">No teammates on leave over this window.</div>`;

  const rosterRows = roster.map((p) => {
    const win = (p.overlaps || []).map((w) => `${w.start_date}→${w.end_date}`).join(', ');
    const tag = p.is_requester ? ' <strong>(this request)</strong>' : (p.has_overlap ? ' <span class="badge warn" style="font-size:9px;">overlap</span>' : '');
    return `<tr><td style="padding:4px 6px;">${_esc(p.name)}${tag}</td><td style="padding:4px 6px;" class="muted">${_esc(p.position || '—')}</td><td style="padding:4px 6px;" class="muted">${_esc(win || '—')}</td></tr>`;
  }).join('');

  const dlg = document.createElement('dialog');
  dlg.style.cssText = 'border:0; border-radius:12px; padding:0; max-width:560px; width:94%;';
  dlg.innerHTML = `
    <div class="card" style="padding:16px 18px; margin:0;">
      <div style="display:flex; justify-content:space-between; align-items:baseline; gap:10px;">
        <div style="font-weight:700; font-size:15px;">Department coverage — ${_esc(dept.name || 'No department')}</div>
        <button type="button" class="btn" data-analysis-close style="padding:2px 10px;">Close</button>
      </div>
      <div class="muted" style="font-size:12px; margin:2px 0 12px;">${_esc(req.employee_name || '')} · ${_esc(req.start_date || '')} → ${_esc(req.end_date || '')}</div>
      ${capBanner}
      ${overlapBox}
      <div style="font-size:11px; text-transform:uppercase; letter-spacing:.04em; opacity:.7; margin:12px 0 4px;">Department roster (${roster.length})</div>
      <div style="max-height:260px; overflow:auto; border:1px solid var(--border-soft, #e5e7eb); border-radius:8px;">
        <table style="width:100%; border-collapse:collapse; font-size:12px;">
          <thead><tr style="text-align:left; opacity:.7;"><th style="padding:4px 6px;">Name</th><th style="padding:4px 6px;">Position</th><th style="padding:4px 6px;">Leave in window</th></tr></thead>
          <tbody>${rosterRows || '<tr><td colspan="3" class="muted" style="padding:6px;">No teammates.</td></tr>'}</tbody>
        </table>
      </div>
    </div>`;
  document.body.appendChild(dlg);
  const close = () => { try { dlg.close(); } catch (e) { /* not open */ } dlg.remove(); };
  dlg.querySelector('[data-analysis-close]').addEventListener('click', close);
  dlg.addEventListener('cancel', (ev) => { ev.preventDefault(); close(); });
  try { dlg.showModal(); } catch (e) { /* no dialog support */ }
}

async function _decideApproval(approvalId, decision) {
  const card = document.querySelector(`[data-appr-card="${approvalId}"]`);
  if (card) card.querySelectorAll('button').forEach((b) => { b.disabled = true; });
  let comments = null;
  if (decision === 'reject') {
    comments = window.prompt('Reason for rejecting this leave request (optional):') || null;
  }
  try {
    const res = await adminAPI.fetch(API.adminEssApprovalDecide(approvalId), {
      method: 'POST', body: JSON.stringify({ decision, comments }),
    });
    const verb = decision === 'approve' ? 'approved' : 'rejected';
    showFlash(`Stage ${verb}. Request is now ${res.request_status}.`, decision === 'approve' ? 'success' : 'error');
    delete _cache.approvals;
    delete _cache.leave;
    delete _cache.loans;
    renderBody();  // re-renders the queue + refreshes the badges
  } catch (err) {
    showFlash(adminAPI.formatErrorDetail ? adminAPI.formatErrorDetail(err) : 'Could not record the decision.', 'error');
    if (card) card.querySelectorAll('button').forEach((b) => { b.disabled = false; });
  }
}

function _renderPolicies(box, data) {
  const policies = (data && data.policies) || [];
  if (!policies.length) {
    box.innerHTML = '<div class="card" style="padding:16px;"><div class="muted">No policies apply to you yet.</div></div>';
    return;
  }
  const items = policies.map((p) => `
    <details class="card ess-policy" data-title="${_esc((p.title || '').toLowerCase())}" style="padding:0; margin-bottom:8px;">
      <summary style="cursor:pointer; padding:10px 12px; font-weight:600;">${_esc(p.title || '(untitled)')}</summary>
      <div style="padding:0 12px 12px; white-space:pre-wrap; font-size:13px; line-height:1.55;">${p.body ? _esc(p.body) : '<span class="muted">No content.</span>'}</div>
    </details>`).join('');
  box.innerHTML = `
    <div class="row" style="align-items:center; gap:8px; margin-bottom:10px;">
      <input id="essPolicySearch" type="search" placeholder="Search policies by title…" autocomplete="off"
             style="font-size:13px; padding:6px 10px; min-width:260px;" />
      <span class="spacer" style="flex:1;"></span>
      <span id="essPolicyCount" class="muted" style="font-size:12px;">${policies.length} polic${policies.length === 1 ? 'y' : 'ies'}</span>
    </div>
    <div class="muted" style="font-size:12px; margin-bottom:8px;">Tap a title to read the policy. These are the policies you're responsible for (and quizzed on).</div>
    <div id="essPolicyList">${items}</div>`;
  const input = document.getElementById('essPolicySearch');
  const countEl = document.getElementById('essPolicyCount');
  if (input) input.addEventListener('input', () => {
    const term = input.value.trim().toLowerCase();
    let shown = 0;
    box.querySelectorAll('.ess-policy').forEach((el) => {
      const match = !term || (el.getAttribute('data-title') || '').includes(term);
      el.style.display = match ? '' : 'none';
      if (match) shown += 1;
    });
    if (countEl) countEl.textContent = term ? `${shown} of ${policies.length}` : `${policies.length} polic${policies.length === 1 ? 'y' : 'ies'}`;
  });
}

const TAB_RENDER = {
  records: _renderRecords,
  training: _renderTraining,
  appraisals: _renderAppraisals,
  policies: _renderPolicies,
  quizzes: _renderQuizzes,
  approvals: _renderApprovals,
};

async function renderBody() {
  const body = document.getElementById('essPortalBody');
  if (!body) return;
  if (_tab === 'leave') {
    if (!_cache.leave) {
      body.innerHTML = '<div class="muted" style="padding:8px;">Loading…</div>';
      try {
        _cache.leave = await adminAPI.fetch(API.adminEssLeave, { method: 'GET', cache: 'no-store' });
      } catch (err) {
        body.innerHTML = `<div class="error" style="padding:8px;">${_esc(adminAPI.formatErrorDetail ? adminAPI.formatErrorDetail(err) : 'Failed to load')}</div>`;
        return;
      }
    }
    _renderLeave(body, _cache.leave);
    return;
  }
  if (_tab === 'loans') {
    if (!_cache.loans) {
      body.innerHTML = '<div class="muted" style="padding:8px;">Loading…</div>';
      try {
        _cache.loans = await adminAPI.fetch(API.adminEssLoans, { method: 'GET', cache: 'no-store' });
      } catch (err) {
        body.innerHTML = `<div class="error" style="padding:8px;">${_esc(adminAPI.formatErrorDetail ? adminAPI.formatErrorDetail(err) : 'Failed to load')}</div>`;
        return;
      }
    }
    _renderLoans(body, _cache.loans);
    return;
  }
  const routeKey = TAB_ROUTES[_tab];
  if (routeKey) {
    if (!_cache[_tab]) {
      body.innerHTML = '<div class="muted" style="padding:8px;">Loading…</div>';
      try {
        _cache[_tab] = await adminAPI.fetch(API[routeKey], { method: 'GET', cache: 'no-store' });
      } catch (err) {
        body.innerHTML = `<div class="error" style="padding:8px;">${_esc(adminAPI.formatErrorDetail ? adminAPI.formatErrorDetail(err) : 'Failed to load')}</div>`;
        return;
      }
    }
    TAB_RENDER[_tab](body, _cache[_tab]);
    return;
  }
  body.innerHTML = '<div class="card" style="padding:16px;"><div class="muted">Coming soon.</div></div>';
}

async function load() {
  Object.keys(_cache).forEach((k) => { delete _cache[k]; });
  const header = document.getElementById('essPortalHeader');
  if (header) header.innerHTML = '<div class="muted">Loading…</div>';
  try {
    _me = await adminAPI.fetch(API.adminEssMe, { method: 'GET', cache: 'no-store' });
  } catch (err) {
    _me = null;
    if (header) {
      const msg = (err && err.status === 403)
        ? 'Your account isn’t linked to an employee record yet — please contact HR.'
        : (adminAPI.formatErrorDetail ? adminAPI.formatErrorDetail(err) : 'Failed to load');
      header.innerHTML = `<div class="card" style="padding:16px;"><div class="muted">${escapeHtml(msg)}</div></div>`;
    }
    return;
  }
  renderHeader();
  _setApprovalBadges((_me && Number(_me.pending_approvals)) || 0);
  renderBody();
}

let _wired = false;
function wire() {
  if (_wired) return;
  const tabs = document.getElementById('essPortalTabs');
  if (!tabs) return;
  _wired = true;
  tabs.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-ess-tab]');
    if (!btn) return;
    const next = btn.getAttribute('data-ess-tab');
    // Leaving a live timed quiz counts as exiting — auto-submit it first.
    if (_activeQuiz && !_activeQuiz.done && next !== 'quizzes') {
      if (!window.confirm('Leaving this tab will submit and score your test now. Continue?')) return;
      _submitActiveQuiz('navigated');
    }
    _tab = next;
    tabs.querySelectorAll('[data-ess-tab]').forEach((b) => b.classList.toggle('active', b === btn));
    renderBody();
  });
}

function mount() {
  wire();
  load();
}

export default { mount, load };
